import { useState, useEffect, useCallback } from 'react';
import { fetchRiskAnalysis } from '@/lib/api';
import { formatMXNCompact, formatDate } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ShieldAlert, AlertTriangle, TrendingDown, DollarSign, Home,
  Calendar, HardHat, CheckCircle2, XCircle, ArrowDown,
} from 'lucide-react';


export default function Validacion() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const result = await fetchRiskAnalysis();
      setData(result);
    } catch {
      toast.error('Error al cargar análisis de riesgo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="space-y-6" data-testid="validacion-loading">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <Skeleton className="h-[500px] rounded-xl" />
    </div>
  );

  if (!data) return null;

  const s = data.summary;
  const riskyCount = s.risk_alto + s.risk_medio;

  return (
    <div className="space-y-6" data-testid="validacion-page">
      <PageHeader
        title="Validación Operativa"
        subtitle="Riesgos operativos y confiabilidad de proyecciones"
      />

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          label="Viviendas en Riesgo"
          value={riskyCount}
          subtitle={`${s.risk_alto} alto · ${s.risk_medio} medio`}
          icon={ShieldAlert}
          className={riskyCount > 0 ? 'border-red-200' : 'border-emerald-200'}
          compact
          testId="kpi-viviendas-riesgo"
        />
        <KPICard
          label="Ingreso en Riesgo"
          value={formatMXNCompact(s.income_at_risk)}
          icon={DollarSign}
          className={s.income_at_risk > 0 ? 'border-amber-200' : ''}
          compact
          testId="kpi-ingreso-riesgo"
        />
        <KPICard
          label="Flujo Optimista"
          value={formatMXNCompact(s.flujo_optimista)}
          icon={TrendingDown}
          compact
          testId="kpi-flujo-optimista"
        />
        <KPICard
          label="Flujo Ajustado"
          value={formatMXNCompact(s.flujo_ajustado)}
          subtitle="Excluyendo ingreso en riesgo"
          icon={ArrowDown}
          className={s.flujo_ajustado < 0 ? 'border-red-200' : ''}
          compact
          testId="kpi-flujo-ajustado"
        />
      </div>

      {/* ── Flow comparison ── */}
      <Card className="rounded-xl border bg-white" data-testid="flow-comparison-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Comparativo de Flujo</p>
            <Badge variant="outline" className="text-[9px] text-slate-400 border-slate-200">
              {s.liberation_issues} inconsistencias en liberaciones
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-slate-500 mb-1">Ingresos Proyectados (Optimista)</p>
              <p className="text-xl font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>
                {formatMXNCompact(s.total_ingresos_proyectados)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#F05A28] mb-1">Ingresos Ajustados (Conservador)</p>
              <p className="text-xl font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>
                {formatMXNCompact(s.total_ingresos_ajustados)}
              </p>
            </div>
          </div>
          {s.income_at_risk > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                <strong>{formatMXNCompact(s.income_at_risk)}</strong> en ingresos dependen de viviendas con riesgo operativo.
                {s.liberation_at_risk > 0 && (
                  <> Además, <strong>{formatMXNCompact(s.liberation_at_risk)}</strong> en liberaciones proyectadas no están respaldadas por avance de obra.</>
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Tabs: Properties / Income / Liberations ── */}
      <Tabs defaultValue="properties" className="space-y-4">
        <TabsList data-testid="risk-tabs">
          <TabsTrigger value="properties">
            Viviendas
            {riskyCount > 0 && <Badge className="ml-1.5 h-4 px-1.5 text-[9px] bg-red-500 text-white">{riskyCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="income">
            Ingresos en Riesgo
            {data.income_at_risk.length > 0 && <Badge className="ml-1.5 h-4 px-1.5 text-[9px] bg-amber-500 text-white">{data.income_at_risk.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="liberations">
            Liberaciones
            {data.liberation_issues.length > 0 && <Badge className="ml-1.5 h-4 px-1.5 text-[9px] bg-amber-500 text-white">{data.liberation_issues.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Properties ── */}
        <TabsContent value="properties">
          <Card className="rounded-xl border bg-white overflow-hidden">
            <Table data-testid="risk-properties-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-20">Riesgo</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Vivienda</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Lt</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Mz</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Escritura</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Proy.</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-3 text-right">Real</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Dif.</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-28">Avance</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Saldo Pend.</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Liberación</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-10">Lib.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.properties.map(p => {
                  const rowBg = p.risk_level === 'alto' ? 'bg-red-50/50' : p.risk_level === 'medio' ? 'bg-amber-50/30' : '';
                  return (
                    <TableRow key={p.property_id} className={`table-row-hover ${rowBg}`} data-testid={`risk-row-${p.property_id}`}>
                      <TableCell className="py-2.5 px-3">
                        <RiskBadge level={p.risk_level} />
                      </TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div>
                          <span className="text-[13px] font-medium text-slate-700">{p.name || '—'}</span>
                          <span className="text-[10px] text-slate-400 ml-1.5">{p.modelo}</span>
                        </div>
                        {p.risk_level !== 'sin_riesgo' && (
                          <p className="text-[10px] text-slate-400 mt-0.5">{p.risk_label}</p>
                        )}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-center text-[13px] tabular-nums">{p.lot}</TableCell>
                      <TableCell className="py-2.5 px-3 text-center text-[13px] font-semibold">{p.block}</TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div className="text-[11px] tabular-nums">
                          {formatDate(p.deed_date)}
                          <span className={`block text-[10px] ${p.days_to_deed < 0 ? 'text-red-500 font-semibold' : p.days_to_deed < 30 ? 'text-amber-600' : 'text-slate-400'}`}>
                            {p.days_to_deed < 0 ? `${Math.abs(p.days_to_deed)}d vencida` : `${p.days_to_deed}d restantes`}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-xs tabular-nums">
                        {p.has_plan ? `${p.projected}%` : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-xs tabular-nums font-bold text-[#F05A28]">
                        {p.has_plan ? `${p.actual}%` : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right">
                        {p.has_plan ? (
                          <span className={`text-[10px] tabular-nums font-semibold ${p.gap >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.gap > 0 ? '+' : ''}{p.gap.toFixed(1)}%
                          </span>
                        ) : <span className="text-slate-300 text-[10px]">—</span>}
                      </TableCell>
                      <TableCell className="py-2.5 px-3">
                        {p.has_plan ? (
                          <div className="space-y-1">
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full rounded-full bg-slate-400" style={{ width: `${Math.min(p.projected, 100)}%` }} />
                            </div>
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full rounded-full bg-[#F05A28]" style={{ width: `${Math.min(p.actual, 100)}%` }} />
                            </div>
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-300">Sin plan</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums text-slate-600">
                        {p.saldo_pendiente > 0 ? formatMXNCompact(p.saldo_pendiente) : <span className="text-emerald-500 text-[10px]">Liquidado</span>}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums text-slate-600">
                        {formatMXNCompact(p.release_amount)}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-center">
                        <Tooltip>
                          <TooltipTrigger>
                            {p.liberation_ok ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-amber-500" />
                            )}
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p className="text-xs max-w-[200px]">{p.liberation_note || 'Liberación respaldada'}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ── Tab: Income at Risk ── */}
        <TabsContent value="income">
          {data.income_at_risk.length === 0 ? (
            <Card className="rounded-xl border bg-white">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-600">Sin ingresos en riesgo</p>
                <p className="text-xs text-slate-400 mt-1">Todos los ingresos proyectados están respaldados.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-xl border bg-white overflow-hidden">
              <Table data-testid="income-risk-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-20">Riesgo</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Vivienda</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Escritura</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Monto en Riesgo</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Razón</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.income_at_risk.map((item, i) => (
                    <TableRow key={i} className="table-row-hover bg-amber-50/20">
                      <TableCell className="py-2.5 px-3"><RiskBadge level={item.risk_level} /></TableCell>
                      <TableCell className="py-2.5 px-3 text-[13px]">{item.label}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[11px] tabular-nums">{formatDate(item.deed_date)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[13px] font-bold tabular-nums text-red-600">{formatMXNCompact(item.amount)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[11px] text-slate-500">{item.reason}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell colSpan={3} className="py-2.5 px-3 text-[11px] uppercase tracking-wider text-slate-500">Total en Riesgo</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[13px] font-bold tabular-nums text-red-600">
                      {formatMXNCompact(data.income_at_risk.reduce((s, i) => s + i.amount, 0))}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab: Liberation Issues ── */}
        <TabsContent value="liberations">
          {data.liberation_issues.length === 0 ? (
            <Card className="rounded-xl border bg-white">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-600">Liberaciones consistentes</p>
                <p className="text-xs text-slate-400 mt-1">Todas las liberaciones están respaldadas por avance de obra.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-xl border bg-white overflow-hidden">
              <Table data-testid="liberation-issues-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Vivienda</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Escritura</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Liberación</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Avance Real</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Inconsistencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.liberation_issues.map((item, i) => (
                    <TableRow key={i} className="table-row-hover">
                      <TableCell className="py-2.5 px-3 text-[13px] font-medium">{item.label}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[11px] tabular-nums">{formatDate(item.deed_date)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums font-semibold">{formatMXNCompact(item.release_amount)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums text-[#F05A28] font-bold">{item.actual}%</TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <XCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          <span className="text-[11px] text-slate-600">{item.note}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}


/* ── Risk Badge ── */
function RiskBadge({ level }) {
  if (level === 'alto') return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[9px]">Alto</Badge>;
  if (level === 'medio') return <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 text-[9px]">Medio</Badge>;
  if (level === 'bajo') return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-[9px]">Bajo</Badge>;
  return <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]">OK</Badge>;
}
